import React, { useState, useContext } from "react";

import { PlusOutlined } from "@ant-design/icons";
import { Button, Drawer } from "antd";

import { AppContext } from "../../AppContext";
import StyledScrollbar from "../Common/UIComponents/StyledScrollbar";
import StyledTooltip from "../Common/UIComponents/StyledTooltip";
import SignOutConfirmation from "../Modals/SignoutConfirmation";
import CompanyList from "./CompanyList";

import {
  getAppVersion, getCustomerConfig, isAddCompanyBlocked, signOut,
} from "../../utils/common";
import { hasPermission, permissions } from "../../utils/Permissions";
import { canViewInvoice, canViewUsageMetrics } from "../MyAccount/UsageMetrics/utils";

import logout from "../../actions/logout";

import { ReactComponent as Close } from "../../assets/images/close.svg";
import { ReactComponent as LogoutIcon } from "../../assets/images/logout.svg";
import { ReactComponent as MyAccountIcon } from "../../assets/images/myAccountIcon.svg";
import "./DrawerNav.css";

const ADD_COMPANY_DISABLED_TOOLTIP = "Please contact CSM to add a company";

export default function DrawerNav({
  handleNavDrawer,
  history,
  navDrawer,
  userInfo,
}) {
  const isAddCompanyDisabled = isAddCompanyBlocked(getCustomerConfig()?.customer_type);
  const canAddCompany = hasPermission(permissions.onboarding.addCompany);
  const showUsageMetrics = canViewUsageMetrics();
  const showInvoice = canViewInvoice();
  const [signOutConfirm, setSignOutConfirm] = useState(false);
  const [logoutHovered, steLogoutHovered] = useState(false);

  const appContext = useContext(AppContext);

  const onSignOutConfirm = async () => {
    setSignOutConfirm(false);
    // Call logout API first before clearing credentials
    await logout()
      .catch(() => {
        // Silently handle any errors - frontend logout proceeds regardless
      })
      .finally(() => {
        // Always call signOut regardless of logout API success/failure
        signOut(history, appContext);
      });
  };

  const onSignOutCancel = () => {
    setSignOutConfirm(false);
  };

  const handleMenuMouseEnter = () => {
    steLogoutHovered(true);
  };

  const handleMenuMouseLeave = () => {
    steLogoutHovered(false);
  };

  const handleAddCompany = () => {
    history.push("/add-company");
    handleNavDrawer();
  };

  const { user_name } = userInfo;
  const accountTitle = user_name ? `Hi, ${user_name}` : "Hi";
  return (
    <Drawer
      placement="right"
      open={navDrawer}
      closable={false}
      onClose={handleNavDrawer}
      width={212}
      className="DrawerNav__Container"
    >
      {signOutConfirm && (
        <SignOutConfirmation
          onConfirm={onSignOutConfirm}
          onCancel={onSignOutCancel}
        />
      )}
      <StyledScrollbar
        className="DrawerNav__ScrollBar"
        noScrollX
        disableTrackYWidthCompensation
        disableTracksWidthCompensation
      >
        <div className="DrawerNav__ScrollContent">
          <div className="DrawerNav__Header">
            <div className="DrawerNav__HeaderAccountInfo">
              <MyAccountIcon className="userIcon" />
              <span className="DrawerNav__UserName" title={accountTitle}>{accountTitle}</span>
            </div>
            <Close
              className="DrawerNav__HeaderCloseButton"
              onClick={handleNavDrawer}
            />
          </div>
          <div className="DrawerNav__RoutesContainer">
            <span
              onClick={() => {
                history.push("/myaccount/profile");
                handleNavDrawer();
              }}
            >
              My Profile
            </span>
            <span
              onClick={() => {
                history.push("/myaccount/members");
                handleNavDrawer();
              }}
            >
              Team Members
            </span>
            {showUsageMetrics && (
              <span
                onClick={() => {
                  history.push("/myaccount/usage-metrics");
                  handleNavDrawer();
                }}
              >
                Usage Metrics
              </span>
            )}
            {showInvoice && (
              <span
                onClick={() => {
                  history.push("/myaccount/invoice");
                  handleNavDrawer();
                }}
              >
                Invoice
              </span>
            )}
            <span
              className="DrawerNav__LogOutButton"
              onClick={() => setSignOutConfirm(true)}
              onMouseEnter={() => handleMenuMouseEnter()}
              onMouseLeave={() => handleMenuMouseLeave()}
            >
              <LogoutIcon className={`DrawerNav__LogOutButton--image ${logoutHovered ? "DrawerNav__LogOutButton--hover" : ""}`} />
              Log Out
            </span>
          </div>
          <div className="DrawerNav__CompaniesContainer">
            <div className="DrawerNav__CompaniesContainer--header">COMPANY</div>
            <CompanyList closeNavbar={handleNavDrawer} navDrawer={navDrawer} />
            {canAddCompany
              && (
                <div className="DrawerNav__AddCompany">
                  {isAddCompanyDisabled ? (
                    <StyledTooltip title={ADD_COMPANY_DISABLED_TOOLTIP} placement="left">
                      <span className="DrawerNav__AddCompany__BtnWrap DrawerNav__AddCompany__BtnWrap--disabled">
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          className="DrawerNav__AddCompany__Btn"
                          disabled
                        >
                          Add Company
                        </Button>
                      </span>
                    </StyledTooltip>
                  ) : (
                    <Button
                      type="primary"
                      onClick={handleAddCompany}
                      icon={<PlusOutlined />}
                      className="DrawerNav__AddCompany__Btn"
                    >
                      Add Company
                    </Button>
                  )}
                </div>
              )}
          </div>
          <div className="DrawerNav__Footer"><p className="DrawerNav__Version">{getAppVersion()}</p></div>
        </div>
      </StyledScrollbar>
    </Drawer>
  );
}
